import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";

const STATUS_CLS = {
  active: "bg-green-50 text-green-700",
  redeemed: "bg-gray-100 text-gray-500",
  pending: "bg-amber-50 text-amber-700",
};

export default function GiftCardsTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/gift-cards").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/gift-cards/${id}`, { status });
      toast.success(status === "redeemed" ? "Marked as used" : "Reactivated");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this gift card permanently?")) return;
    await api.delete(`/admin/gift-cards/${id}`);
    load();
  };

  const sold = items.filter((g) => g.status !== "pending");
  const active = items.filter((g) => g.status === "active");

  return (
    <div data-testid="admin-gift-cards">
      <div className="flex flex-wrap gap-6 mb-6 text-sm text-[#4A4A4A]">
        <p><span className="font-medium">{active.length}</span> active</p>
        <p><span className="font-medium">{items.filter((g) => g.status === "redeemed").length}</span> redeemed</p>
        <p><span className="font-medium">${sold.reduce((s, g) => s + (g.amount || 0), 0).toLocaleString()}</span> total sold</p>
      </div>
      <p className="text-xs text-[#B5B5B0] mb-6">Clients mention their code when booking — mark the card Redeemed when you apply it. Cards never expire.</p>
      <div className="space-y-3">
        {items.map((g) => (
          <div key={g.id} className="bg-white border border-[#E5E5E2] p-5 flex flex-wrap items-center gap-x-6 gap-y-3" data-testid={`gift-row-${g.id}`}>
            <div className="min-w-[130px]">
              <p className="font-mono text-sm tracking-wider" data-testid={`gift-code-${g.id}`}>{g.code}</p>
              <p className="text-xs text-[#7A7A7A] mt-0.5">${(g.amount || 0).toLocaleString()}</p>
            </div>
            <div className="text-xs text-[#4A4A4A] min-w-[180px]">
              <p>Buyer: {g.buyer_name} · {g.buyer_email}</p>
              {g.recipient_name && <p className="mt-1">For: {g.recipient_name} · {g.recipient_email}</p>}
            </div>
            <p className="text-xs text-[#B5B5B0]">{(g.created_at || "").slice(0, 10)}</p>
            <span className={`text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 ${STATUS_CLS[g.status] || STATUS_CLS.pending}`} data-testid={`gift-status-${g.id}`}>
              {g.status}
            </span>
            <div className="flex gap-2 ml-auto">
              {g.status === "active" && (
                <button onClick={() => setStatus(g.id, "redeemed")} data-testid={`gift-redeem-${g.id}`}
                  className="text-[11px] uppercase tracking-[0.12em] border border-[#E5E5E2] px-4 py-2 hover:border-[#1A1A1A] transition-colors">
                  Mark Redeemed
                </button>
              )}
              {g.status === "redeemed" && (
                <button onClick={() => setStatus(g.id, "active")} data-testid={`gift-reactivate-${g.id}`}
                  className="text-[11px] uppercase tracking-[0.12em] border border-[#E5E5E2] px-4 py-2 hover:border-[#1A1A1A] transition-colors">
                  Reactivate
                </button>
              )}
              <button onClick={() => remove(g.id)} data-testid={`gift-delete-${g.id}`} aria-label="Delete"
                className="px-3 border border-[#E5E5E2] hover:border-red-300 hover:text-red-600 transition-colors"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-[#7A7A7A] text-center py-10">No gift cards sold yet — share your Gift Cards page link.</p>}
      </div>
    </div>
  );
}
